/**
 * TransitHorizon - Celestial Events Arc Timeline
 * 
 * Displays upcoming celestial events on a curved arc (horizon).
 * More visually distinctive than horizontal scroll, works better on mobile.
 * Events sit on the arc with the "Now" marker on the left.
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Moon, Sun, Star, Zap } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getAuthHeaders } from '@/utils/api';

// Event type to icon mapping
const getEventIcon = (type) => {
    switch (type) {
        case 'eclipse':
            return Moon;
        case 'ingress':
            return Star;
        case 'conjunction':
            return Sparkles;
        case 'retrograde':
            return Zap;
        case 'alignment':
            return Sun;
        default:
            return Star;
    }
};

// Event type to color mapping
const getEventColor = (type, importance) => {
    if (importance === 'major' || importance === 'generational') {
        return { primary: '#E0AA3E', glow: '#E0AA3E40' };
    }
    switch (type) {
        case 'eclipse':
            return { primary: '#C084FC', glow: '#C084FC40' };
        case 'retrograde':
            return { primary: '#F87171', glow: '#F8717140' };
        case 'ingress':
            return { primary: '#60A5FA', glow: '#60A5FA40' };
        default:
            return { primary: '#E0AA3E', glow: '#E0AA3E40' };
    }
};

const TransitHorizon = ({ 
    events = [], 
    onEventClick,
    className = "" 
}) => {
    const { language } = useLanguage();
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchedEvents, setFetchedEvents] = useState([]);
    const [hasFetched, setHasFetched] = useState(false);
    
    const isSpanish = language === 'es';
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    
    // Fetch events only once if not provided (with delay to stagger API calls)
    useEffect(() => {
        // If events are provided externally, use those
        if (events.length > 0) {
            setFetchedEvents(events);
            return;
        }
        
        // Only fetch once if no events provided
        if (hasFetched) return;
        
        // Stagger API calls - delay 200ms to let critical requests go first
        const timeoutId = setTimeout(async () => {
            setIsLoading(true);
            setHasFetched(true);
            try {
                const headers = await getAuthHeaders();
                const response = await fetch(`${backendUrl}/api/celestial/events?count=5`, {
                    credentials: 'include',
                    headers
                });
                if (response.ok) {
                    const data = await response.json();
                    setFetchedEvents(data.events || []);
                }
            } catch (err) {
                console.error('Failed to fetch events:', err);
            }
            setIsLoading(false);
        }, 200);  // 200ms delay to stagger API calls
        
        return () => clearTimeout(timeoutId);
    }, [hasFetched, backendUrl, events.length]);
    
    // Calculate days until event
    const getDaysUntil = (dateStr) => {
        const eventDate = new Date(dateStr);
        const now = new Date();
        const diffTime = eventDate - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };
    
    // Format date
    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString(isSpanish ? 'es-MX' : 'en-US', {
            month: 'short',
            day: 'numeric'
        });
    };
    
    // Calculate position on arc (0 = left/now, 1 = right/future)
    const getArcPosition = (index, total) => {
        return (index + 0.5) / total;
    };
    
    const displayEvents = fetchedEvents.slice(0, 5);
    
    return (
        <div className={`relative ${className}`}>
            {/* Section Header */}
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-primary/70 text-xs font-inter uppercase tracking-wider flex items-center gap-2">
                    <Sparkles className="w-3 h-3" />
                    {isSpanish ? 'Horizonte de Tránsitos' : 'Transit Horizon'}
                </h3>
                <span className="text-primary/40 text-[10px] font-mono">
                    {isSpanish ? 'PRÓXIMOS EVENTOS' : 'UPCOMING EVENTS'}
                </span>
            </div>
            
            {isLoading ? (
                <div className="flex items-center justify-center h-48">
                    <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                </div>
            ) : (
                <div className="relative h-56 overflow-hidden px-2">
                    {/* SVG Arc - adjusted viewBox for better edge visibility */}
                    <svg 
                        viewBox="-10 0 420 220" 
                        className="w-full h-full"
                        preserveAspectRatio="xMidYMax meet"
                    >
                        {/* Gradient definitions */}
                        <defs>
                            <linearGradient id="arcGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#E0AA3E" stopOpacity="0.8" />
                                <stop offset="50%" stopColor="#E0AA3E" stopOpacity="0.4" />
                                <stop offset="100%" stopColor="#E0AA3E" stopOpacity="0.1" />
                            </linearGradient>
                            <filter id="glow">
                                <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                                <feMerge>
                                    <feMergeNode in="coloredBlur" />
                                    <feMergeNode in="SourceGraphic" />
                                </feMerge>
                            </filter>
                        </defs>
                        
                        {/* The Arc Path */}
                        <motion.path
                            d="M 20 180 Q 200 20 380 180"
                            fill="none"
                            stroke="url(#arcGradient)"
                            strokeWidth="2"
                            strokeDasharray="8 4"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 1.5, ease: "easeOut" }}
                        />
                        
                        {/* "Now" Marker */}
                        <g transform="translate(20, 180)">
                            <motion.circle
                                r="8"
                                fill="#E0AA3E"
                                filter="url(#glow)"
                                initial={{ scale: 0 }}
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{ duration: 2, repeat: Infinity }}
                            />
                            <text 
                                y="25" 
                                textAnchor="middle" 
                                fill="#E0AA3E" 
                                fontSize="10"
                                fontFamily="monospace"
                            >
                                {isSpanish ? 'HOY' : 'NOW'}
                            </text>
                        </g>
                        
                        {/* Event Nodes on Arc */}
                        {displayEvents.map((event, index) => {
                            const position = getArcPosition(index, displayEvents.length);
                            // Calculate point on quadratic bezier curve
                            const t = position;
                            const x = Math.pow(1-t, 2) * 20 + 2 * (1-t) * t * 200 + Math.pow(t, 2) * 380;
                            const y = Math.pow(1-t, 2) * 180 + 2 * (1-t) * t * 20 + Math.pow(t, 2) * 180;
                            
                            const Icon = getEventIcon(event.type);
                            const colors = getEventColor(event.type, event.importance);
                            const daysUntil = getDaysUntil(event.date);
                            const isWithin24h = daysUntil <= 1;
                            
                            return (
                                <g 
                                    key={event.date + event.event}
                                    transform={`translate(${x}, ${y})`}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                        setSelectedEvent(selectedEvent?.date === event.date ? null : event);
                                        onEventClick?.(event);
                                    }}
                                >
                                    {/* Glow effect for imminent events */}
                                    {isWithin24h && (
                                        <motion.circle
                                            r="20"
                                            fill={colors.glow}
                                            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0.2, 0.5] }}
                                            transition={{ duration: 2, repeat: Infinity }}
                                        />
                                    )}
                                    
                                    {/* Event circle */}
                                    <motion.circle
                                        r="14"
                                        fill="#0B0D17"
                                        stroke={colors.primary}
                                        strokeWidth="2"
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ delay: index * 0.15 + 0.5 }}
                                        whileHover={{ scale: 1.2 }}
                                    />
                                    
                                    {/* Icon placeholder (using text for SVG) */}
                                    <text
                                        textAnchor="middle"
                                        dominantBaseline="central"
                                        fill={colors.primary}
                                        fontSize="12"
                                    >
                                        {event.type === 'eclipse' ? '☽' : 
                                         event.type === 'ingress' ? '★' :
                                         event.type === 'retrograde' ? '℞' :
                                         event.type === 'conjunction' ? '☌' : '✦'}
                                    </text>
                                    
                                    {/* Date label */}
                                    <text
                                        y="28"
                                        textAnchor="middle"
                                        fill="#E0AA3E"
                                        fontSize="9"
                                        fontFamily="monospace"
                                        opacity="0.8"
                                    >
                                        {formatDate(event.date)}
                                    </text>
                                </g>
                            );
                        })}
                    </svg>
                    
                    {/* Selected Event Details Panel - Positioned at TOP to not block circles */}
                    <AnimatePresence>
                        {selectedEvent && (
                            <motion.div
                                className="absolute left-0 right-0 p-4 rounded-xl z-10
                                    top-0 sm:top-auto sm:bottom-0 
                                    bg-gradient-to-b from-black via-black/95 to-transparent sm:bg-gradient-to-t sm:from-transparent sm:via-black/95 sm:to-black"
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedEvent(null);
                                }}
                            >
                                <div className="flex items-start gap-3">
                                    <div 
                                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                        style={{ 
                                            backgroundColor: `${getEventColor(selectedEvent.type, selectedEvent.importance).primary}20`,
                                            border: `1px solid ${getEventColor(selectedEvent.type, selectedEvent.importance).primary}40`
                                        }}
                                    >
                                        {(() => {
                                            const Icon = getEventIcon(selectedEvent.type);
                                            return <Icon className="w-5 h-5" style={{ color: getEventColor(selectedEvent.type, selectedEvent.importance).primary }} />;
                                        })()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-amber-400 text-xs font-mono">
                                                {formatDate(selectedEvent.date)}
                                            </span>
                                            {getDaysUntil(selectedEvent.date) <= 7 && (
                                                <span className="px-2 py-0.5 bg-amber-500/20 rounded text-amber-400 text-[10px] font-mono">
                                                    {getDaysUntil(selectedEvent.date) <= 1 
                                                        ? (isSpanish ? 'INMINENTE' : 'IMMINENT')
                                                        : `${getDaysUntil(selectedEvent.date)} ${isSpanish ? 'días' : 'days'}`
                                                    }
                                                </span>
                                            )}
                                            {/* Tap hint */}
                                            <span className="text-primary/30 text-[10px] ml-auto">
                                                {isSpanish ? 'Toca para cerrar' : 'Tap to close'}
                                            </span>
                                        </div>
                                        <h4 className="text-primary font-cinzel text-sm mb-1">
                                            {selectedEvent.event}
                                        </h4>
                                        <p className="text-primary/60 text-xs font-inter line-clamp-2">
                                            {selectedEvent.mystical_message || selectedEvent.short_description}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            )}
        </div>
    );
};

export default TransitHorizon;
